import React, { useCallback, useState, useRef } from 'react';
import {
  FolderIcon,
  DocumentTextIcon,
  PhotoIcon,
  TableCellsIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { useAdminTranslation } from '../../../../../modules/i18n';
import { WizardUploadFile } from '../../../../../types/upload';
import { FolderDropResult } from '../hooks/useSimplifiedUploadWizard';

interface FolderDropStepProps {
  markdownFiles: WizardUploadFile[];
  assetFiles: WizardUploadFile[];
  sheetFiles: WizardUploadFile[];
  validationErrors: Array<{ code: number; message: string }>;
  validationWarnings: string[];
  onFolderDrop: (files: FileList | File[]) => Promise<FolderDropResult>;
  onClearFiles: () => void;
}

export const FolderDropStep: React.FC<FolderDropStepProps> = ({
  markdownFiles,
  assetFiles,
  sheetFiles,
  validationErrors,
  validationWarnings,
  onFolderDrop,
  onClearFiles
}) => {
  const { t } = useAdminTranslation();
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropErrors, setDropErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasFiles = markdownFiles.length > 0 || assetFiles.length > 0 || sheetFiles.length > 0;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      setIsProcessing(true);
      setDropErrors([]);

      try {
        const result = await onFolderDrop(files);
        if (result.errors.length > 0) {
          setDropErrors(result.errors);
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [onFolderDrop]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const items = e.dataTransfer.items;
      const files: File[] = [];

      // Handle folder drop with DataTransferItemList
      if (items) {
        const promises: Promise<void>[] = [];

        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry?.();
            if (entry) {
              promises.push(traverseFileTree(entry, files));
            } else {
              // Fallback for browsers that don't support webkitGetAsEntry
              const file = item.getAsFile();
              if (file) files.push(file);
            }
          }
        }

        await Promise.all(promises);
      }

      if (files.length > 0) {
        await processFiles(files);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- traverseFileTree defined below; forward reference avoided intentionally
    [processFiles]
  );

  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        await processFiles(files);
      }
      // Reset input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [processFiles]
  );

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleClear = useCallback(() => {
    setDropErrors([]);
    onClearFiles();
  }, [onClearFiles]);

  // Helper to traverse file tree for folder drops
  const traverseFileTree = useCallback(async (entry: FileSystemEntry, files: File[]): Promise<void> => {
    if (entry.isFile) {
      const fileEntry = entry as FileSystemFileEntry;
      return new Promise((resolve) => {
        fileEntry.file(
          (file) => {
            // Create a new file object with the webkitRelativePath set
            Object.defineProperty(file, 'webkitRelativePath', {
              value: entry.fullPath.replace(/^\//, ''),
              writable: false
            });
            files.push(file);
            resolve();
          },
          () => resolve() // Ignore errors for individual files
        );
      });
    } else if (entry.isDirectory) {
      const dirEntry = entry as FileSystemDirectoryEntry;
      const reader = dirEntry.createReader();

      return new Promise((resolve) => {
        const readEntries = () => {
          reader.readEntries(
            async (entries) => {
              if (entries.length === 0) {
                resolve();
                return;
              }

              const entryPromises = entries.map((e) => traverseFileTree(e, files));
              await Promise.all(entryPromises);

              // Continue reading (readEntries may not return all at once)
              readEntries();
            },
            () => resolve()
          );
        };
        readEntries();
      });
    }
  }, []);

  return (
    <div className='space-y-6'>
      {/* Drop zone */}
      {!hasFiles && (
        <div
          className={`relative flex min-h-[300px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed transition-all ${
            isDragOver
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-gray-100 dark:border-border-dark dark:bg-dark-background-secondary dark:hover:border-primary/50 dark:hover:bg-dark-background-hover'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {isProcessing ? (
            <div className='flex flex-col items-center space-y-4'>
              <div className='h-12 w-12 animate-spin rounded-full border-4 border-primary/30 border-t-primary' />
              <p className='text-sm text-gray-600 dark:text-dark-text-secondary'>
                {t('content.documents.wizard.processingLabel')}
              </p>
            </div>
          ) : (
            <>
              <FolderIcon className='mb-4 h-16 w-16 text-gray-400' />
              <p className='mb-2 text-lg font-medium text-gray-700 dark:text-dark-text-secondary'>
                {t('content.documents.wizard.dropFolder')}
              </p>
              <p className='text-sm text-gray-500 dark:text-dark-text-secondary'>
                {t('content.documents.wizard.folderRequirements')}
              </p>
              <p className='mt-4 text-xs text-gray-400'>
                {t('content.documents.wizard.supportedFormats')}
              </p>
            </>
          )}

          {/* Hidden file input with directory support */}
          <input
            ref={inputRef}
            type='file'
            className='hidden'
            /* @ts-expect-error - webkitdirectory is not in HTML spec but widely supported */
            webkitdirectory='true'
            directory='true'
            multiple
            onChange={handleInputChange}
          />
        </div>
      )}

      {/* File summary (shown after drop) */}
      {hasFiles && (
        <div className='space-y-4'>
          {/* Header with clear button */}
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <CheckCircleIcon className='h-5 w-5 text-green-500' />
              <span className='font-medium text-gray-900 dark:text-dark-text'>
                {t('content.documents.wizard.folderSelected')}
              </span>
            </div>
            <button
              onClick={handleClear}
              className='flex items-center space-x-1 text-sm text-gray-500 hover:text-red-500 dark:text-dark-text-secondary dark:hover:text-red-400'
            >
              <XMarkIcon className='h-4 w-4' />
              <span>{t('content.documents.wizard.clearFiles')}</span>
            </button>
          </div>

          {/* File count cards */}
          <div className='grid grid-cols-3 gap-4'>
            {/* Markdown files */}
            <div className='flex items-center space-x-3 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20'>
              <DocumentTextIcon className='h-8 w-8 text-blue-500' />
              <div>
                <p className='text-2xl font-bold text-blue-700 dark:text-blue-400'>
                  {markdownFiles.length}
                </p>
                <p className='text-sm text-blue-600 dark:text-blue-500'>
                  {t('content.documents.wizard.markdownFiles')}
                </p>
              </div>
            </div>

            {/* Asset files */}
            <div className='flex items-center space-x-3 rounded-lg bg-pink-50 p-4 dark:bg-pink-900/20'>
              <PhotoIcon className='h-8 w-8 text-pink-500' />
              <div>
                <p className='text-2xl font-bold text-pink-700 dark:text-pink-400'>
                  {assetFiles.length}
                </p>
                <p className='text-sm text-pink-600 dark:text-pink-500'>
                  {t('content.documents.wizard.assetFiles')}
                </p>
              </div>
            </div>

            {/* Sheet files */}
            <div className='flex items-center space-x-3 rounded-lg bg-green-50 p-4 dark:bg-green-900/20'>
              <TableCellsIcon className='h-8 w-8 text-green-500' />
              <div>
                <p className='text-2xl font-bold text-green-700 dark:text-green-400'>
                  {sheetFiles.length}
                </p>
                <p className='text-sm text-green-600 dark:text-green-500'>
                  {t('content.documents.wizard.sheetFiles')}
                </p>
              </div>
            </div>
          </div>

          {/* Validation errors */}
          {validationErrors.length > 0 && (
            <div className='rounded-lg bg-red-50 p-4 dark:bg-red-900/20'>
              <div className='flex items-start space-x-2'>
                <ExclamationCircleIcon className='h-5 w-5 flex-shrink-0 text-red-500' />
                <div className='space-y-1'>
                  <p className='font-medium text-red-700 dark:text-red-400'>
                    {t('content.documents.wizard.validationErrors') || 'Lỗi xác thực'}:
                  </p>
                  <ul className='list-inside list-disc space-y-1 text-sm text-red-600 dark:text-red-500'>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Validation warnings */}
          {validationWarnings.length > 0 && (
            <div className='rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/20'>
              <div className='flex items-start space-x-2'>
                <ExclamationTriangleIcon className='h-5 w-5 flex-shrink-0 text-yellow-500' />
                <div className='space-y-1'>
                  <p className='font-medium text-yellow-700 dark:text-yellow-400'>
                    {t('content.documents.wizard.validationWarnings') || 'Cảnh báo'}:
                  </p>
                  <ul className='list-inside list-disc space-y-1 text-sm text-yellow-600 dark:text-yellow-500'>
                    {validationWarnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Errors */}
      {dropErrors.length > 0 && (
        <div className='rounded-lg bg-red-50 p-4 dark:bg-red-900/20'>
          <div className='flex items-start space-x-2'>
            <ExclamationCircleIcon className='h-5 w-5 flex-shrink-0 text-red-500' />
            <div className='space-y-1'>
              <p className='font-medium text-red-700 dark:text-red-400'>
                {t('content.documents.wizard.errors')}:
              </p>
              <ul className='list-inside list-disc space-y-1 text-sm text-red-600 dark:text-red-500'>
                {dropErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
